import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Play, Pause, Maximize2, Minimize2, RotateCcw } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ScrollingScreenshotPreviewProps {
  src: string;
  alt?: string;
  enableAutoScroll?: boolean;
  className?: string;
  height?: string | number;
  scrollDuration?: number; // Duration in seconds for full scroll
}

export const ScrollingScreenshotPreview: React.FC<ScrollingScreenshotPreviewProps> = ({
  src,
  alt = 'Screenshot do projeto',
  enableAutoScroll = true,
  className,
  height = '500px',
  scrollDuration = 15 // 15 seconds for full scroll
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const animationRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const pausedProgressRef = useRef<number>(0);

  const [isScrolling, setIsScrolling] = useState(enableAutoScroll);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [progress, setProgress] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    setIsScrolling(enableAutoScroll);
  }, [enableAutoScroll]);

  // Easing function for smooth animation
  const easeInOutCubic = (t: number): number => {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  };

  const animate = useCallback((timestamp: number) => {
    if (!containerRef.current || !imageRef.current) return;

    if (startTimeRef.current === null) {
      startTimeRef.current = timestamp;
    }

    const container = containerRef.current;
    const maxScroll = container.scrollHeight - container.clientHeight;

    if (maxScroll <= 0) {
      animationRef.current = requestAnimationFrame(animate);
      return;
    }

    const elapsed = (timestamp - startTimeRef.current) * speed;
    const duration = scrollDuration * 1000; // Convert to milliseconds
    const rawProgress = (pausedProgressRef.current * duration + elapsed) / duration;

    // Use linear progress but apply easing only at start and end
    let currentProgress = rawProgress % 1;

    // Apply easing at boundaries for smooth start/stop
    if (currentProgress < 0.05) {
      currentProgress = easeInOutCubic(currentProgress * 20) * 0.05;
    } else if (currentProgress > 0.95) {
      const remaining = 1 - currentProgress;
      currentProgress = 1 - easeInOutCubic(remaining * 20) * 0.05;
    }

    // Reset when reaching the end with smooth transition
    if (rawProgress >= 1) {
      startTimeRef.current = timestamp;
      pausedProgressRef.current = 0;
    }

    container.scrollTop = currentProgress * maxScroll;
    setProgress(currentProgress * 100);

    animationRef.current = requestAnimationFrame(animate);
  }, [speed, scrollDuration]);

  useEffect(() => {
    if (isScrolling && imageLoaded) {
      animationRef.current = requestAnimationFrame(animate);
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isScrolling, imageLoaded, animate]);

  const toggleScrolling = () => {
    if (isScrolling) {
      // Pausing - save current progress
      if (containerRef.current) {
        const container = containerRef.current;
        const maxScroll = container.scrollHeight - container.clientHeight;
        if (maxScroll > 0) {
          pausedProgressRef.current = container.scrollTop / maxScroll;
        }
      }
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    } else {
      // Resuming - reset start time
      startTimeRef.current = null;
    }
    setIsScrolling(!isScrolling);
  };

  const resetScroll = () => {
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
      pausedProgressRef.current = 0;
      startTimeRef.current = null;
      setProgress(0);
    }
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const handleWheel = (e: React.WheelEvent) => {
    if (containerRef.current) {
      e.preventDefault();
      // Pause auto-scroll when user manually scrolls
      if (isScrolling) {
        setIsScrolling(false);
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
        }
      }
      containerRef.current.scrollTop += e.deltaY;
    }
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  return (
    <div className={cn(
      'relative group bg-slate-950 rounded-xl overflow-hidden border border-slate-800',
      isFullscreen && 'fixed inset-0 z-50 rounded-none',
      className
    )}>
      {/* Progress bar */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-slate-800 z-20">
        <div
          className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 transition-all duration-100"
          style={{ width: `${progress}%` }}
        />
      </div>

      <div
        ref={containerRef}
        className={cn(
          'overflow-y-auto overflow-x-hidden relative bg-slate-900 scrollbar-hide',
          isFullscreen ? 'h-screen' : ''
        )}
        style={{
          height: isFullscreen ? '100vh' : height,
        }}
        onWheel={handleWheel}
      >
        <img
          ref={imageRef}
          src={src}
          alt={alt}
          className="w-full h-auto"
          style={{ display: 'block' }}
          draggable={false}
          onLoad={handleImageLoad}
        />
      </div>

      {/* Controls overlay */}
      <div className={cn(
        'absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-4',
        'flex items-center justify-between gap-3',
        'transition-opacity duration-300',
        'opacity-0 group-hover:opacity-100'
      )}>
        <div className="flex items-center gap-3">
          <Button
            size="sm"
            variant="secondary"
            onClick={toggleScrolling}
            className="h-9 px-3 bg-white/10 hover:bg-white/20 border-0"
          >
            {isScrolling ? (
              <Pause className="w-4 h-4" />
            ) : (
              <Play className="w-4 h-4 ml-0.5" />
            )}
          </Button>

          <Button
            size="sm"
            variant="secondary"
            onClick={resetScroll}
            className="h-9 px-3 bg-white/10 hover:bg-white/20 border-0"
          >
            <RotateCcw className="w-4 h-4" />
          </Button>

          <div className="flex items-center gap-2 ml-2">
            <span className="text-xs text-white/60 font-medium">Velocidade:</span>
            <div className="flex gap-1">
              {[0.5, 1, 2, 3].map((s) => (
                <button
                  key={s}
                  onClick={() => setSpeed(s)}
                  className={cn(
                    'w-8 h-8 rounded-lg text-xs font-semibold transition-all duration-200',
                    speed === s
                      ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/30'
                      : 'bg-white/10 text-white/60 hover:bg-white/20 hover:text-white'
                  )}
                >
                  {s}x
                </button>
              ))}
            </div>
          </div>
        </div>

        <Button
          size="sm"
          variant="secondary"
          onClick={toggleFullscreen}
          className="h-9 px-3 bg-white/10 hover:bg-white/20 border-0"
        >
          {isFullscreen ? (
            <Minimize2 className="w-4 h-4" />
          ) : (
            <Maximize2 className="w-4 h-4" />
          )}
        </Button>
      </div>

      {/* Auto-scroll badge */}
      {enableAutoScroll && (
        <div className={cn(
          'absolute top-4 right-4 px-3 py-1.5 rounded-full text-xs font-semibold',
          'bg-gradient-to-r from-blue-600 to-indigo-600 text-white',
          'shadow-lg shadow-blue-500/30',
          'transition-opacity duration-300',
          isScrolling ? 'opacity-100' : 'opacity-50'
        )}>
          {isScrolling ? '▶ Auto-scroll' : '⏸ Pausado'}
        </div>
      )}

      {/* Play button overlay when paused */}
      {!isScrolling && (
        <div
          className="absolute inset-0 flex items-center justify-center cursor-pointer z-10"
          onClick={toggleScrolling}
        >
          <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center backdrop-blur-md border border-white/20 hover:bg-white/20 transition-all duration-300">
            <Play className="w-6 h-6 text-white ml-1" />
          </div>
        </div>
      )}
    </div>
  );
};
